/**
 * editing operations
 * 
 * @author    Jens Gruschel, Maurizio Tidei
 * @copyright © 2018 contexagon GmbH
 */


import {openPageAction} from "../redux/view/actions";
import {
    MEDIA_UPLOAD_PAGE,
    OBJECT_EDIT_PAGE,
    OBJECT_LIST_PAGE
} from "../redux/view/consts";
import {pushStateAction} from "../redux/navigation/actions";
import {showCurrentFilter} from "./listing";
import {
    REST_CALL_LOAD_OBJECT,
    REST_CALL_LOAD_ALL_TAGS,
    REST_CALL_SAVE_OBJECT,
    REST_SERVER_BASE_URL
} from "../config";
import {showMessageAction, showDialogMessageAction} from "../redux/message/actions";
import {showError} from "./message";
import {
    popEditStackAction,
    assignElementAction,
    addTagAction,
    changeTabAction,
    createNewObjectAction,
    createNewObjectReferenceAction,
    createNewElementAction,
    deleteTagAction,
    loadObjectAction,
    pushElementAction,
    saveObjectSuccessAction,
    switchObjectAction,
    loadAllTagsSuccessAction,
    fetchReferencedObjectsAction,
    fetchReferencableObjectsAction,
    updateObjectMapAction
} from "../redux/editing/actions";
import {fetchJSONActivity, showJSONResponse} from "./communication";
import {fetchObjectList, excludeDeleted} from "./objects";
import {requestNextSequenceValues} from "./sequences";
import {selectReferencedObjectIds} from '../redux/types/selectors';
import {selectObjectById} from "../redux/editing/selectors";
import {selectObjectTitle, selectFieldsWithDefaultValues, selectFieldValue, selectSequenceFieldDefs} from "../redux/types/selectors";
import {selectTranslation} from "../redux/configuration/selectors";
import {ACCESS_WRITABLE} from "../redux/authorization/consts";


export function createNewObject(typeId) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("create new object"));

        // create new object
        const fields = await getPredefinedFields(getState(), typeId);
        dispatch(createNewObjectAction(typeId, fields));

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function createNewMediaItem() {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("create new media item"));

        // create new media item
        const fields = await getPredefinedFields(getState(), "mediaitem");
        dispatch(createNewObjectAction("mediaitem", fields));

        // load existing media items
        await fetchReferencableObjects("mediaitem", {})(dispatch, getState);

        // open page
        dispatch(openPageAction(MEDIA_UPLOAD_PAGE));
    }

}

export function selectReference(fieldPath, itemTypeId, constraint, flags = {}) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("select reference"));

        // load existing objects of the given type
        await fetchReferencableObjects(itemTypeId, constraint)(dispatch, getState);

        // create new object reference
        const fields = await getPredefinedFields(getState(), itemTypeId);
        dispatch(createNewObjectReferenceAction(fieldPath, itemTypeId, fields, flags));

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function addElement(fieldPath, itemTypeId, fields = {}) {

    return (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("add element"));

        // create new element item
        const defaultFields = selectFieldsWithDefaultValues(getState().types, itemTypeId);
        dispatch(createNewElementAction(fieldPath, itemTypeId, {...defaultFields, ...fields}));

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function refreshObjectReferences(object) {

    return async (dispatch, getState) => {
        loadReferencedObjects(dispatch, getState, object);
    }
}

export function editObject(objectId) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("edit object", "?object=" + objectId));

        // load object
        await loadObject(dispatch, getState, objectId, {clearStack: true});

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function pushAndEditObject(objectId, readOnly) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("edit object", "?object=" + objectId));

        // load object
        await loadObject(dispatch, getState, objectId, {clearStack: false, readOnly: readOnly});

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function pushAndEditElement(fieldPath, element, readOnly) {

    return (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("edit element"));

        // extract element
        dispatch(pushElementAction(fieldPath, element, {readOnly: readOnly}));

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function duplicateObject(objectId) {

    return async (dispatch, getState) => {

        // push new state
        dispatch(pushStateAction("duplicate object"));

        // load object for cloning
        await loadObject(dispatch, getState, objectId, {clone: true, clearStack: true});

        // open page
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

async function getPredefinedFields(state, objectTypeId) {

    const defaultFields = selectFieldsWithDefaultValues(state.types, objectTypeId);
    const sequenceFieldDefs = selectSequenceFieldDefs(state.types, objectTypeId);
    if (sequenceFieldDefs.length === 0) {
        return defaultFields;
    }
    else {
        const sequenceFields = await requestNextSequenceValues(
            sequenceFieldDefs,
            state.authorization.accessToken
        );
        return {...defaultFields, ...sequenceFields};
    }
}

async function saveObjectToServer(dispatch, description, object, token) {

    return await fetchJSONActivity(dispatch, description,
        REST_SERVER_BASE_URL + REST_CALL_SAVE_OBJECT.path, {
            method: REST_CALL_SAVE_OBJECT.method,
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token
            },
            body: JSON.stringify(object)
        }
    );
}

function completeObject(state, object) {
    const translate = (value) => selectTranslation(state.configuration, value);
    return {
        ...object,
        title: selectObjectTitle(state.types, state.editing.objects, object, translate)
    };
}

export function saveObject(object, close) {

    return async (dispatch, getState) => {

        const stackSize = getState().editing.editStack.length;
        console.log("saveObject", object);

        try {
            const state = getState();
        
            const json = await saveObjectToServer(dispatch, "saving object", completeObject(state, object), state.authorization.accessToken);
            const modifiedObject = json.data;
            const accessLevel = json.accesslevel;
            const newObjectId = modifiedObject ? modifiedObject.id : undefined;

            dispatch(saveObjectSuccessAction(modifiedObject));
            dispatch(showMessageAction("Speichern erfolgreich."));

            // close editing view or otherwise remain in current view
            if (close) {
                if (stackSize > 1) {
                    // dispatch(pushStateAction("???")); // TODO: push state?
                    dispatch(popEditStackAction());
                }
                else {
                    await showCurrentFilter()(dispatch, getState);
                    dispatch(pushStateAction("show objects"));
                    dispatch(openPageAction(OBJECT_LIST_PAGE));
                }
            }
            else if (newObjectId) {
                // object ID might have been changed by server
                dispatch(switchObjectAction(newObjectId, accessLevel || ACCESS_WRITABLE)) // TODO: remove ACCESS_WRITABLE if server really returns this value
            }
        }
        catch (error) {
            showError(error)(dispatch);
        }
    }
}

export function saveAndAssignReference(newObject) {

    return async (dispatch, getState) => {
        try {
            const state = getState();

            const json = await saveObjectToServer(dispatch, "saving object", completeObject(state, newObject), state.authorization.accessToken);
            const modifiedObject = json.data;
            const newObjectId = modifiedObject ? modifiedObject.id : undefined;

            dispatch(saveObjectSuccessAction(modifiedObject));
            if (newObjectId) {
                dispatch(assignElementAction(newObjectId));
                dispatch(updateObjectMapAction([modifiedObject]));
                dispatch(showMessageAction("Speichern und zuweisen erfolgreich."));
            }
            else {
                showError("could not attach object")(dispatch);
            }
        }
        catch (error) {
            showError(error)(dispatch);
        }
    }
}

export function assignElement(value) {

    return (dispatch, getState) => {
        const state = getState();
        dispatch(assignElementAction(completeObject(state, value)));
    }
}

export function cancelWithoutSaving() {

    return async (dispatch, getState) => {

        const stackSize = getState().editing.editStack.length;

        if (stackSize > 1) {
            // dispatch(pushStateAction("???")); // TODO: push state?
            dispatch(popEditStackAction());
        }
        else {
            await showCurrentFilter()(dispatch, getState);
            dispatch(pushStateAction("show objects"));
            dispatch(openPageAction(OBJECT_LIST_PAGE));
        }
    }
}

async function loadReferencedObjects(dispatch, getState, object) {
    let referencedObjectIds = selectReferencedObjectIds(getState().types, object);
    if (object.objectaccess) {
        if (object.objectaccess.readableFor) {
            referencedObjectIds = [...referencedObjectIds, ...object.objectaccess.readableFor];
        }
        if (object.objectaccess.writableFor) {
            referencedObjectIds = [...referencedObjectIds, ...object.objectaccess.writableFor];
        }
    }
    console.log("loading referenced objects", referencedObjectIds);
    if (referencedObjectIds.length > 0) {
        await fetchReferencedObjects(referencedObjectIds)(dispatch, getState);
    }
}

async function loadObject(dispatch, getState, id, params = {}) {

    try {
        const { authorization, configuration } = getState();
        const translate = (value) => selectTranslation(configuration, value);

        const response = await fetchJSONActivity(dispatch, "loading object",
            REST_SERVER_BASE_URL + REST_CALL_LOAD_OBJECT.path.replace("<id>", id), {
                method: REST_CALL_LOAD_OBJECT.method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + authorization.accessToken
                }
            }
        );

        const loadedObject = response.data;
        const accessLevel = response.accesslevel;
        const fieldsetAccessLevels = response.fieldsetaccesslevels;
        const referencedBy = response.referencedBy;
        console.log("loaded object", loadedObject, "access level", accessLevel, "referenced by", referencedBy);

        if (params.clone === true) {

            // remove object sepcific information
            delete loadedObject.id;
            delete loadedObject['objectaccess']
            delete loadedObject['user-created']
            delete loadedObject['user-modified']
            delete loadedObject['timestamp-created']
            delete loadedObject['timestamp-modified']

            // add "(copy)" to title or name
            for (const fieldName in {title: '', name: ''}) {
                if (fieldName in loadedObject.fields) {
                    loadedObject.fields[fieldName] = translate(loadedObject.fields[fieldName]) + " (copy)";
                    break;
                }
            }

            dispatch(loadObjectAction(loadedObject, {
                accessLevel: ACCESS_WRITABLE,
                referencedBy: [],
                fieldsetAccessLevels: fieldsetAccessLevels
            }, params));    
        }
        else {

            dispatch(loadObjectAction(loadedObject, {
                accessLevel: accessLevel,
                referencedBy: referencedBy,
                fieldsetAccessLevels: fieldsetAccessLevels
            }, params));    
        }
    
        await loadReferencedObjects(dispatch, getState, loadedObject);

        // load all tags asynchronously (await not needed)
        // noinspection JSIgnoredPromiseFromCall
        loadAllTags(dispatch, getState);
    }
    catch (error) {
        showError(error)(dispatch);
    }
}

async function loadAllTags(dispatch, getState) {

    try {
        const { authorization } = getState();

        const json = await fetchJSONActivity(dispatch, "loading tags",
            REST_SERVER_BASE_URL + REST_CALL_LOAD_ALL_TAGS.path, {
                method: REST_CALL_LOAD_ALL_TAGS.method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + authorization.accessToken
                }
            }
        );

        let loadedTags = json.tags;
        console.log("loaded tags:", loadedTags);

        dispatch(loadAllTagsSuccessAction(loadedTags));
    }
    catch (error) {
        showError(error)(dispatch);
    }
}

function fetchReferencableObjects(objectTypeId, constraint = {}) {
    return async (dispatch, getState) => {
        try {
            const objectList = await fetchObjectList(
                dispatch,
                getState,
                excludeDeleted(
                    {"type": objectTypeId, ...constraint},
                    {"timestamp-modified": -1}
                )
            );
            dispatch(fetchReferencableObjectsAction(objectList));
        }
        catch (error) {
            dispatch(fetchReferencableObjectsAction([]));
            showError(error)(dispatch);
        }
    }
};

function fetchReferencedObjects(objectIds) {
    return async (dispatch, getState) => {
        if (objectIds.length > 0) {
            try {
                // search for deleted and non-deleted objects, because otherwise references to deleted objects are invisible
                const objectList = await fetchObjectList(dispatch, getState, {"id": {"$in": objectIds}});
                dispatch(fetchReferencedObjectsAction(objectList));
            }
            catch (error) {
                showError(error)(dispatch);
            }
        }
    }
}

export function popAndAddReference(referenceObjectId) {
    return dispatch => {
        // TODO: push new state?
        // dispatch(pushStateAction("edit object"));
        dispatch(assignElementAction(referenceObjectId));
        dispatch(openPageAction(OBJECT_EDIT_PAGE));
    }
}

export function deleteTag(objectId, index) {

    return dispatch => {
        console.log("deleteTag, objectId:", objectId, "index:", index);
        dispatch(deleteTagAction(objectId, index));
    };
}

export function addTag(objectId, tag) {

    return dispatch => {
        console.log("addTag, objectId:", objectId, "tag:", tag);
        dispatch(addTagAction(objectId, tag));
    };
}

/**
 * change tab
 * @param objectId  object identifier
 * @param tabKey    key of the tab set
 * @param index     new tab index
 * @returns {Function}
 * @author          Jens Gruschel
 */
export function changeTab(tabKey, index) {
    return dispatch => {
        dispatch(changeTabAction(tabKey, index));
    }
}

/**
 * complete string by filling placeholders with current field values
 * @param state     state
 * @param object    object
 * @param template  string containing placeholders
 * @returns         {string}
 * @author          Maurizio Tidei, Jens Gruschel
 */
function completePlaceholders(state, object, template) {
    if (template.match(/^<[^>]+>$/)) {
        // single field? no uri encoding
        return selectFieldValue(state.types, state.editing.objects, object, template.slice(1, -1), false);
    }
    return template.replace(/<(.+?)>/g, (match, submatch) => {
        const fieldValue = selectFieldValue(state.types, state.editing.objects, object, submatch, false);
        if (fieldValue === undefined || fieldValue === null) return '';
        else if (typeof fieldValue === "object") return encodeURIComponent(JSON.stringify(fieldValue));
        else return encodeURIComponent(fieldValue);
    });
}

/**
 *
 * @param objectId
 * @param resturl
 * @param restmethod
 * @returns {Function}
 * @author Maurizio Tidei
 */
export function executeObjectCommand(objectId, resturl, restmethod, bodyDef, onFinish) {

    return async (dispatch, getState) => {

        console.log("executing command", restmethod, resturl);

        const state = getState();
        const object = selectObjectById(state.editing, objectId);

        // replace field names within URL by field values
        const resturlFilled = completePlaceholders(state, object, resturl);

        // provide field values as request body
        let bodyValue;
        if (typeof bodyDef === "string" && bodyDef.length > 0)
        {
            if (bodyDef.substr(0, 1) === "{" && bodyDef.substr(-1, 1) === "}") {
                // make an object with defined fields
                const parts = bodyDef.slice(1, -1).split(",").map(s => s.trim());
                bodyValue = {}
                parts.forEach(part => {
                    const colonIndex = part.indexOf(":");
                    const field = colonIndex >= 0 ? part.substr(colonIndex + 1).trim() : part.trim();
                    const name = colonIndex >= 0 ? part.substr(0, colonIndex).trim() : field;
                    bodyValue[name] = selectFieldValue(state.types, state.editing.objects, object, field, false);
                });
            }
            else if (bodyDef.substr(0, 1) === "[" && bodyDef.substr(-1, 1) === "]") {
                // make an array
                const parts = bodyDef.slice(1, -1).split(",").map(s => s.trim());
                bodyValue = parts.map(part => selectFieldValue(state.types, state.editing.objects, object, part), false);
            }
            else {
                // simply pass the given field
                bodyValue = selectFieldValue(state.types, state.editing.objects, object, bodyDef.trim(), false);
            }
        }

        try {
            const response = await fetchJSONActivity(dispatch, "executing command",
                REST_SERVER_BASE_URL + resturlFilled, {
                    method: restmethod,
                    body: bodyValue ? JSON.stringify(bodyValue) : undefined,
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + state.authorization.accessToken
                    }
                }
            );
            showJSONResponse(dispatch, "command response", response);
        }
        catch (error) {
            if (onFinish) onFinish();
            console.log(error);
            dispatch(showDialogMessageAction("Error executing Command", error.message));
        }
        finally {
            if (onFinish) onFinish();
        }
    }
}

export function completeFrameURL(objectId, url) {
    return (dispatch, getState) => {

        const state = getState();
        const object = selectObjectById(state.editing, objectId);

        // replace field names within URL by field values
        // console.log("frame URL", url, completePlaceholders(state, object, url));
        return completePlaceholders(state, object, url);
    }
}

export function completeLabel(objectId, value) {
    return (dispatch, getState) => {

        const state = getState();
        const object = selectObjectById(state.editing, objectId);

        // replace field names within value by field values
        // console.log("label", value, completePlaceholders(state, object, value));
        return completePlaceholders(state, object, value);
    }
}
